// CAR-111 — web personalized health summary. A deterministic, PHI-free
// reflection of the public BMI-quiz answers, shown on /start in place of a
// generic thank-you: it mirrors the visitor's own situation back to them
// ("we understand why it's been hard"), frames weight as medical rather than
// a failure of willpower, and hands off to a (generic, un-named) doctor as
// the expert. No clinical claims, no diagnosis, no eligibility judgement —
// the quiz never gates medical eligibility (that's the intake). Pure +
// locale-aware so the web renderer and tests share one source of copy.
//
// NOTE(product): this WAS engineering-placeholder copy awaiting a clinical
// pass. For the v2 path (F-01, `buildV2Summary`) that pass has happened:
// Ö-138 supplied the ingress and the three reflection modules
// (Ö-138), and they are canonical now — do not reword them.
//
// The legacy v1 path below (old leads with no BMI) is still placeholder text,
// and the original note stands for it: the mapping is the durable part, the
// strings are meant to be swapped.

import { z } from "zod";
import type { Locale } from "./common";
import {
  bmiQuizV1,
  computeBmiFromAnswers,
  DESIRED_SUPPORT_KEY,
  roundBmi,
  type BmiQuizAnswerValue,
} from "./bmi-quiz";
import {
  buildF01ResultPage,
  F01_MEETING_OUTCOMES,
  F01_RESULT_COPY,
  type F01ResultPage,
} from "./f01-result-page";
import {
  F01_SAVED_FEEDBACK_OPENING,
  isF01OptionalTrackOutcome,
} from "./f01-forward-copy";
import {
  flattenPriceOverview,
  formatKronor,
  priceOverview,
  priceOverviewBlockSchema,
} from "./price-overview";

// TES-1126 — one of the three closed, expandable sections after the primary
// CTA on a positive new-treatment result (Emelie 2026-10-06): "told" is
// "Det du har berättat för oss", "how" is "Så går det till härifrån", "cost"
// is the shared price overview (TES-1459, price-overview.ts). A client renders
// `paragraphs`, then `steps` as a numbered list, then `notes`, in that order;
// any of them may be empty. The copy is server-side so it can change without
// an app release.
//
// TES-1459 — `blocks` carries the price overview's headed blocks. A client
// that knows it renders the blocks in place of `paragraphs`; `paragraphs`
// still holds the same text flattened (each heading a line of its own), so
// app <= 2.0.11, which strips the unknown key, shows it all in order.
export const f01ResultSectionSchema = z.object({
  key: z.enum(["told", "how", "cost"]),
  title: z.string(),
  paragraphs: z.array(z.string()),
  steps: z.array(z.string()),
  notes: z.array(z.string()),
  blocks: z.array(priceOverviewBlockSchema).optional(),
  // TES-1462 — the cost section's campaign box (price-overview.ts
  // `campaignBox`), rendered at the head of the section and visible while it
  // is closed. Only with `F01ResultPricing.separateCampaign`, and then
  // `paragraphs` starts with the box's lines.
  campaign: priceOverviewBlockSchema.optional(),
});
export type F01ResultSection = z.infer<typeof f01ResultSectionSchema>;

/**
 * TES-1126 — which channel a result is built for. The web's "how" section
 * opens with the hand-off line into the app; the app's does not. Everything
 * else is shared.
 */
export type F01ResultChannel = "web" | "app";

/**
 * TES-1126 — the amounts the cost section names, in minor units (hundredths
 * of a krona), from the tenant config's `pricing`. Omitted, the result has
 * no cost section rather than a hardcoded price.
 */
export interface F01ResultPricing {
  coreSubscriptionMinor: number;
  /** TES-1459 — the maintenance plan; without it the overview omits that block. */
  maintenanceMinor?: number;
  labTestMinor: number;
  /**
   * Ö-359 — months free at sign-up while the launch offer runs: the API's
   * SUBSCRIPTION_FREE_MONTHS (TES-809), which the web reads from the same
   * env. Only above 0 does the cost section mention the free month (see
   * price-overview.ts); omitted or 0, it does not, so it is never a
   * permanent term.
   */
  launchOfferFreeMonths?: number;
  /**
   * TES-1462 — the client renders the campaign box apart from the regular
   * price list (CLIENT_CONTRACT_CHANGES.campaignBox), so the cost section
   * carries the offer as `campaign` instead of inside Uppstart.
   */
  separateCampaign?: boolean;
}

export const healthProfileSummarySchema = z.object({
  heading: z.string(),
  reflections: z.array(z.string()),
  expertNote: z.string(),
  // F-01 v2 — the BMI-banded suitability indication (A ≥30 and E 25,0–29,9,
  // both positive new-treatment outcomes that book a care meeting (TES-1066,
  // TES-1115), C <25 usually not first option, D missing data). Absent for
  // legacy v1 answer sets. B (27–29,9) is retired as a letter: 27 is a copy
  // boundary inside E now (`newTreatment.bandText`), not a routing line.
  //
  // E is a new letter rather than a re-banded C on purpose: the app's
  // F01Screen maps C to "Tillbaka till hem" with booking as the secondary
  // button, and every letter other than C and D to the A/B branch, with
  // "Boka vårdmöte" as the primary CTA. A new letter reaches that CTA in app builds
  // that already exist; a re-banded C would have needed a new one.
  variant: z.enum(["A", "C", "D", "E"]).optional(),
  bmi: z.number().nullable().optional(),
  // Health form v3 §4.4 ("Hälsoformulär v3") — which RESULT this is, separate
  // from the BMI band.
  //
  // The prescription-transfer branch gets its own result view and
  // "får inte visa boknings-CTA, mötesbetalning eller ordinarie F-01-resultatmappning".
  // That is deliberately NOT a fifth variant value: consumers switch on
  // `variant` with an A/B fallthrough that ends in "boka vårdmöte", so a new
  // enum member would have shown the forbidden booking CTA in every branch
  // nobody remembered to update. A separate field forces the check to be
  // explicit.
  track: z
    .enum(["standard", "prescription_transfer"])
    .optional()
    .default("standard"),
  // TES-1144 (TES-1112 points 1 and 9) — the transfer result's BMI line,
  // "Ditt beräknade BMI är [x,x], utifrån den längd och vikt du har angett."
  // (Ö-360), under the main heading. Set on the transfer track only, and only
  // when her height and weight give a BMI; a positive new-treatment result
  // carries its line in `newTreatment.bmiText` instead. Information only: the
  // transfer track is routed by `track`, never by BMI, and gets no band text.
  bmiText: z.string().optional(),
  // TES-1115 (Ö-335 answered "b", Emelie 2026-10-05) — the transfer result's
  // routing line under its main heading: the heading orients, this line says
  // what happens next. Set on the transfer track only.
  routingHeading: z.string().optional(),
  // TES-1115 — Emelie's result hierarchy for a positive new-treatment outcome
  // (TES-1112 points 2–3), one field per block so a client can render them in
  // her order: heading → `bmiText` → `reflection` → `meetingValue` → `price`
  // → the one CTA → `safety`. `bandText` is the short BMI explanation, the
  // only part that differs between 25,0–26,9, 27,0–29,9 and 30+. Set for
  // variants A and E only. `price` is null when the caller did not pass the
  // consult fee (it is never hardcoded here).
  newTreatment: z
    .object({
      bmiText: z.string(),
      bandText: z.string(),
      reflection: z.string().nullable(),
      meetingValue: z.string(),
      price: z.string().nullable(),
      safety: z.string(),
      // TES-1126 — Emelie's result spec v2 (2026-10-06): the subheading
      // between the BMI block and the meeting value, and the three closed,
      // expandable sections that follow the primary CTA. Optional so a
      // payload from an older API still parses in a client that knows them.
      nextStepHeading: z.string().optional(),
      sections: z.array(f01ResultSectionSchema).optional(),
    })
    .optional(),
});
export type HealthProfileSummary = z.infer<typeof healthProfileSummarySchema>;

/**
 * TES-1107 (Ö-329, Emelie 2026-10-05): for BMI 25–29.9 (variant E) the
 * "Din återkoppling" disclosure is open from the start, on the web /start
 * result and in the app's F-01 result, so that what leads to the
 * "Boka vårdmöte" CTA is never hidden. Every other result keeps it closed.
 *
 * TES-1115: the web's positive new-treatment result no longer has the box
 * (Emelie 2026-10-05: no expandable boxes), so only the app reads this now;
 * its own redesign is app 2.0.6 (TES-1112 S5).
 */
export function f01FeedbackOpenByDefault(
  summary: Pick<HealthProfileSummary, "variant">,
): boolean {
  return summary.variant === "E";
}

type Answers = Record<string, BmiQuizAnswerValue>;

// Durations that read as "I've struggled with this for years".
const LONG_DURATIONS = new Set(["1_to_3y", "3_to_5y", "more_than_5y"]);
// Cap reflections so the page stays a glance, not a wall of text.
const MAX_REFLECTIONS = 4;

function asArray(value: BmiQuizAnswerValue | undefined): string[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

// Resolve a multi/single-select answer's raw values to their localized
// option labels (lowercased for mid-sentence use), dropping the catch-all
// "other"/"none" buckets that don't read well in prose.
function selectedLabels(
  fieldKey: string,
  answers: Answers,
  locale: Locale,
): string[] {
  const field = bmiQuizV1.fields.find((f) => f.key === fieldKey);
  if (!field?.options) return [];
  return asArray(answers[fieldKey])
    .filter((v) => v !== "other" && v !== "none")
    .map((v) => field.options!.find((o) => o.value === v)?.label[locale])
    .filter((l): l is string => Boolean(l))
    .map((l) => l.toLowerCase());
}

// "a, b och c" / "a, b and c".
function joinList(items: string[], locale: Locale): string {
  if (items.length <= 1) return items[0] ?? "";
  const head = items.slice(0, -1).join(", ");
  const last = items[items.length - 1]!;
  return locale === "en" ? `${head} and ${last}` : `${head} och ${last}`;
}

// TES-191 (health declaration part 1, "Hälsodeklaration Del 1") — the
// profile-insight taxonomy. The approved part 1 copy ends with
// "Vi ser att din viktresa framför allt verkar påverkas av…" and four themes.
// We map the biggestObstacles (multi) + hardestPart (single) answers onto
// those themes and surface the dominant one.
type ObstacleTheme = "biology" | "stress" | "behaviour" | "self_image";

// Spec tie-break order (Biology → Stress → Behaviour → Self-image).
const OBSTACLE_THEME_ORDER: ObstacleTheme[] = [
  "biology",
  "stress",
  "behaviour",
  "self_image",
];

// Each obstacle/hardest value maps to the theme it most signals.
const OBSTACLE_VALUE_THEME: Record<string, ObstacleTheme> = {
  hunger_cravings: "biology",
  stress_eating: "stress",
  emotions_without_food: "stress",
  regain: "behaviour",
  lose_momentum: "behaviour",
  sustaining_habits: "behaviour",
  knowing_what_works: "behaviour",
  time_energy: "behaviour",
  self_criticism: "self_image",
  deprioritize_self: "self_image",
};

function dominantObstacleTheme(answers: Answers): ObstacleTheme | null {
  const values = [
    ...asArray(answers.biggestObstacles),
    ...asArray(answers.hardestPart),
  ];
  const counts = new Map<ObstacleTheme, number>();
  for (const v of values) {
    const theme = OBSTACLE_VALUE_THEME[v];
    if (theme) counts.set(theme, (counts.get(theme) ?? 0) + 1);
  }
  if (counts.size === 0) return null;
  const max = Math.max(...counts.values());
  // Deterministic tie-break by spec order.
  return OBSTACLE_THEME_ORDER.find((t) => counts.get(t) === max) ?? null;
}

const OBSTACLE_THEME_REFLECTION: Record<
  ObstacleTheme,
  { sv: string; en: string }
> = {
  biology: {
    sv: "Vi ser att din viktresa framför allt verkar påverkas av hunger och sug – det styrs av kroppens biologi, och det är just där medicinsk behandling kan göra skillnad.",
    en: "We see your weight journey seems mainly shaped by hunger and cravings — that's driven by your body's biology, and it's exactly where medical treatment can make a difference.",
  },
  stress: {
    sv: "Vi ser att din viktresa framför allt verkar påverkas av hur stress och känslor regleras via mat – något vården kan hjälpa dig med, inte något att skämmas för.",
    en: "We see your weight journey seems mainly shaped by how stress and emotions get regulated through food — something care can help with, not something to be ashamed of.",
  },
  behaviour: {
    sv: "Vi ser att din viktresa framför allt verkar påverkas av hur svårt det är att hålla i nya vanor över tid – det handlar om biologi och sammanhang, inte bristande vilja.",
    en: "We see your weight journey seems mainly shaped by how hard it is to sustain new habits over time — that's about biology and context, not a lack of will.",
  },
  self_image: {
    sv: "Vi ser att din viktresa framför allt verkar påverkas av hur hård du är mot dig själv – du förtjänar omtanke, inte skam.",
    en: "We see your weight journey seems mainly shaped by how hard you are on yourself — you deserve care, not shame.",
  },
};

export function buildHealthProfileSummary(input: {
  answers: Answers;
  locale: Locale;
  /**
   * TES-798 — omit the ingress from `reflections`. Set for app builds that
   * print an identical line of their own above the summary; leaving it in
   * makes the patient read the same sentence twice. Defaults to false, so
   * the web and every caller that does not know about client builds keeps
   * the full summary. See CLIENT_CONTRACT_CHANGES.f01SummaryIngress.
   */
  omitIngress?: boolean;
  /**
   * TES-1115 — the digital care meeting's fee in minor units (hundredths of a krona), for the
   * price line of a positive new-treatment result. The API passes
   * CONSULT_FEE_CENTS, the amount it charges; the web passes the tenant
   * config's `consultationMinor`, which must equal it. Omitted, the result
   * carries no price line rather than a hardcoded one.
   */
  consultFeeMinor?: number;
  /** TES-1126 — see F01ResultChannel. Defaults to the app (the API's caller). */
  channel?: F01ResultChannel;
  /** TES-1126 — see F01ResultPricing. */
  pricing?: F01ResultPricing;
}): HealthProfileSummary {
  const {
    answers,
    locale,
    omitIngress = false,
    consultFeeMinor,
    channel = "app",
    pricing,
  } = input;
  const en = locale === "en";

  // F-01 v2 answer sets (weightKg present) get the PDF's variant result +
  // signal-area reflections. Legacy v1 sets keep the original build below —
  // an old lead can never produce a BMI, so it never claims an indication.
  if (typeof answers.weightKg === "string") {
    return buildV2Summary(answers, locale, omitIngress, {
      consultFeeMinor,
      channel,
      pricing,
    });
  }

  const reflections: string[] = [];

  // 0. Profile insight — the Del 1 headline ("Vi ser att din viktresa…").
  // Leads the summary when the obstacle questions were answered.
  const theme = dominantObstacleTheme(answers);
  if (theme) {
    reflections.push(OBSTACLE_THEME_REFLECTION[theme][en ? "en" : "sv"]);
  }

  // 1. Effort + duration — lead with recognition, not advice.
  const methods = selectedLabels("methodsTried", answers, locale);
  const longTried = LONG_DURATIONS.has(String(answers.howLongTried ?? ""));
  if (methods.length > 0) {
    const list = joinList(methods, locale);
    reflections.push(
      en
        ? `You've already tried ${list}. Weight is driven by biology, not willpower — and a medical approach can change the outcome.`
        : `Du har redan provat ${list}. Vikt styrs av biologi, inte viljestyrka – och med medicinsk hjälp kan utfallet bli ett annat.`,
    );
  } else if (longTried) {
    reflections.push(
      en
        ? "You've been working at this for a long time. That persistence matters — what's often missing is medical, not motivational."
        : "Du har kämpat med det här länge. Den uthålligheten betyder något – det som ofta saknats är medicinskt, inte motivation.",
    );
  }
  if (methods.length > 0 && longTried) {
    reflections.push(
      en
        ? "Trying for years without lasting results is common — a reason to get medical support, not to give up."
        : "Att försöka i åratal utan bestående resultat är vanligt – ett skäl att få medicinsk hjälp, inte att ge upp.",
    );
  }

  // 2. Impact on life — acknowledge what they told us.
  const impact = selectedLabels("impactOnLife", answers, locale);
  if (impact.length > 0) {
    const list = joinList(impact, locale);
    reflections.push(
      en
        ? `You told us it affects your ${list}. This is about reclaiming those parts of life.`
        : `Du berättade att det påverkar din ${list}. Det här handlar om att få tillbaka de delarna av livet.`,
    );
  }

  // 3. Goals — connect to a tailored plan.
  const goals = selectedLabels("whyLoseWeight", answers, locale);
  if (goals.length > 0) {
    const list = joinList(goals, locale);
    reflections.push(
      en
        ? `Your goals — ${list} — are exactly what a tailored treatment is built around.`
        : `Dina mål – ${list} – är precis det en anpassad behandling byggs kring.`,
    );
  }

  // 4. Emotional eating — gentle, only when it's a real factor (4–5).
  if (Number(answers.emotionalEating) >= 4) {
    reflections.push(
      en
        ? "Using food to cope is human, and it's something care can support — not something to be ashamed of."
        : "Att använda mat för att hantera känslor är mänskligt, och något vården kan stötta – inget att skämmas för.",
    );
  }

  // Never render empty, even for a sparse quiz.
  if (reflections.length === 0) {
    reflections.push(
      en
        ? "Lasting weight change is medical, not a matter of willpower — and you don't have to figure it out alone."
        : "Bestående viktförändring är medicinsk, inte en fråga om viljestyrka – och du behöver inte lösa det ensam.",
    );
  }

  return {
    heading: en
      ? "We understand why it's been hard"
      : "Vi förstår varför det varit svårt",
    reflections: reflections.slice(0, MAX_REFLECTIONS),
    expertNote: en
      ? "A licensed doctor reviews your profile and recommends a treatment suited to you — there's no one-size-fits-all."
      : "En legitimerad läkare går igenom din profil och rekommenderar en behandling som passar dig – det finns ingen universallösning.",
    // Legacy v1 answer sets predate the branch entirely.
    track: "standard",
  };
}

// ---------------------------------------------------------------------------
// F-01 v2 — the result summary + personal reflection modules. Since TES-1161
// every result's copy is the canonical page's (f01-result-page.ts); this file
// carries it into the summary fields older app builds render, and keeps the
// personal reflection, which is not result copy.
// ---------------------------------------------------------------------------

// Weight or height missing: the only result without a canonical page (the
// clients send her back to the quiz to complete it).
const VARIANT_COPY: Record<
  "D",
  Record<"sv" | "en", { heading: string; body: string; nextStep: string }>
> = {
  D: {
    sv: {
      heading: "Vi behöver lite mer information för att vägleda dig",
      body: "För att kunna ge en första BMI-baserad vägledning behöver vi både vikt och längd. Utan det kan vi fortfarande spegla delar av din viktresa, men inte säga om medicinsk viktbehandling verkar relevant att undersöka.",
      nextStep: "Fyll i vikt och längd för att få din första vägledning.",
    },
    en: {
      heading: "We need a bit more information to guide you",
      body: "To give an initial BMI-based guidance we need both weight and height. Without them we can still reflect parts of your weight journey, but not say whether medical weight treatment seems relevant to explore.",
      nextStep: "Fill in weight and height to get your first guidance.",
    },
  },
};

// TES-1126 — the three closed sections after the primary CTA. Every Swedish
// string is verbatim from an approved source; none is ours:
//   - titles and the web's opening line of "how": the F-01 doc's
//     "Expanderbar sektion 1–3";
//   - "how"'s five steps: since TES-1161 the shared meeting module's
//     (f01-result-page.ts), passed in, with no closing note: the module's body
//     already says treatment is not guaranteed and that no subscription
//     starts. Clients that render the module open (the web, app 2.0.7) leave
//     this section out; app 2.0.6 still shows it closed;
//   - "cost": since TES-1459 the shared price overview (price-overview.ts),
//     the one source the booking flow and the Hem card also read.
// The app gets the same cost section as information, with no purchase action
// (the doc: "ingen extern abonnemangsbetalningslänk eller köpknapp").
// English is ours (proposed, not approved).
const RESULT_SECTION_COPY = {
  sv: {
    toldTitle: "Det du har berättat för oss",
    howTitle: "Så går det till härifrån",
    howWebIntro: "Fortsätt i Ella-appen. Dina svar följer med.",
  },
  en: {
    toldTitle: "What you have told us",
    howTitle: "How it works from here",
    howWebIntro: "Continue in the Ella app. Your answers come with you.",
  },
} as const;

/**
 * TES-1115 — the BMI as she is shown it: always one decimal, with a decimal
 * comma in Swedish ("26,1", "30,0").
 */
export function formatBmi(bmi: number, locale: Locale): string {
  const text = bmi.toFixed(1);
  return locale === "en" ? text : text.replace(".", ",");
}

/**
 * TES-1115 — a fee in minor units the way the price line shows it: "395 kr"
 * or "SEK 395". Whole kronor, formatted like the web's `formatKr`.
 */
export function formatConsultFee(minor: number, locale: Locale): string {
  return formatKronor(minor, locale);
}

// Ö-138 — her three reflection modules, verbatim.
//
// What they replaced: five paragraphs a developer wrote as placeholders, all
// opening with "Du beskriver". The file's own comment said so outright — the
// text was "engineering-placeholder" that "should get a clinical/marketing
// pass", and "the strings are meant to be swapped". Up to three were shown
// at once, so the patient met two or three paragraphs in a row opening with
// the same three words. It read like a template, not like someone who had
// listened.
//
// Her correction is not a synonym swap: each module got its OWN opening
// ("Du berättar…", "Du har också uppgett…", "När det gäller vardagen…"), and
// that is what lets them stand one after another.
//
// ALL FIVE ARE NOW HERS. Categories 4 and 5 stood empty for a short while —
// she had only written three — and she sent the last two on 2026-09-15 with
// the message "Vi behåller samtliga fem kategorier".
//
// TES-1658 — F-01 v13 asks the same themes with new questions. Each module
// reads the v13 answers that carry its theme next to the v12 ones it always
// read, so every older answer set keeps its reflection:
//   1 hunger, fullness, cravings  ← weightDifficulties hunger_fullness,
//                                   cravings_food_thoughts
//   2 body or life phase          ← weightChangeLifePhase's four phases,
//                                   hormonalLifePhase perimenopause/after
//   3 making change last          ← weightDifficulties changes_dont_last,
//                                   regain, time_routines
//   4 food and stress or emotions ← weightDifficulties stress_emotions_pause
// The module copy itself is unchanged.
function hasAny(
  raw: BmiQuizAnswerValue | undefined,
  values: readonly string[],
): boolean {
  return asArray(raw).some((v) => values.includes(v));
}

// TES-1126 — the F-01 doc's later "SLUTLIG KANONISK KORRIGERING —
// F-01-ÅTERKOPPLINGENS INGRESS OCH MODULINLEDNINGAR" gives modules 1, 3 and 4
// openings without a "Du berättar"/"Du beskriver" prefix
// ("Kroppens signaler…", "I vardagen verkar…", "Mat kan ibland…"); they are
// verbatim below. Modules 2 and 5 are not in that list and keep their text.
//
// TES-1320 — `direction` marks module 5 ("Riktning och motivation"): the
// desired-support block goes right before it (see desiredSupportReflection).
const V2_MODULES: Array<{
  applies: (a: Answers) => boolean;
  copy: Record<"sv" | "en", string>;
  direction?: true;
}> = [
  {
    applies: (a) =>
      asArray(a.bodySignals).some((v) => v !== "none_of_these") ||
      hasAny(a.weightDifficulties, [
        "hunger_fullness",
        "cravings_food_thoughts",
      ]),
    copy: {
      sv: "Kroppens signaler kring hunger, mättnad eller sug har påverkat din viktresa. Det är en viktig del att förstå, eftersom vikt sällan bara handlar om kunskap eller disciplin.",
      en: "Your body's signals around hunger, fullness or cravings have shaped your weight journey. That's important to understand — weight is rarely just about knowledge or discipline.",
    },
  },
  {
    applies: (a) =>
      asArray(a.changeContext).some((v) =>
        ["after_pregnancy", "with_age", "hormonal_changes"].includes(v),
      ) ||
      asArray(a.lifeStage).some((v) =>
        ["after_pregnancy", "perimenopause", "after_menopause"].includes(v),
      ) ||
      hasAny(a.weightChangeLifePhase, [
        "pregnancy",
        "periods_changed",
        "perimenopause",
        "after_menopause",
      ]) ||
      hasAny(a.hormonalLifePhase, ["perimenopause", "after_menopause"]),
    copy: {
      sv: "Du har också uppgett att kroppen eller livsfasen kan ha förändrat förutsättningarna. Ella drar inga slutsatser om hormoner utifrån de här svaren, men informationen är relevant att ta med i den fortsatta bedömningen.",
      en: "You have also said that your body or life stage may have changed the conditions. Ella draws no conclusions about hormones from these answers, but the information is relevant to bring into the continued assessment.",
    },
  },
  {
    applies: (a) =>
      asArray(a.biggestObstacles).some((v) =>
        [
          "regain",
          "strong_start_fades",
          "works_until_stress",
          "routines_hard",
        ].includes(v),
      ) ||
      hasAny(a.weightDifficulties, [
        "changes_dont_last",
        "regain",
        "time_routines",
      ]),
    copy: {
      sv: "I vardagen verkar utmaningen inte bara vara att börja, utan att få förändringen att hålla över tid. Det är den typen av mönster Ella vill förstå för att stödet ska bli mer användbart.",
      en: "In everyday life the challenge seems to be not just starting, but making the change last over time. That's the kind of pattern Ella wants to understand to make the support more useful.",
    },
  },
  {
    applies: (a) =>
      a.emotionalEating === "yes_often" ||
      a.emotionalEating === "yes_sometimes" ||
      hasAny(a.weightDifficulties, ["stress_emotions_pause"]),
    copy: {
      sv: "Mat kan ibland hänga ihop med stress, trötthet eller känslor. Det är viktig information, eftersom ett hållbart stöd behöver ta hänsyn till både maten och situationerna runt omkring.",
      en: "Food can sometimes be tied to stress, tiredness or emotions. That is important information, because sustainable support has to account for both the food and the situations around it.",
    },
  },
  {
    // "Riktning och motivation".
    //
    // Her rule: category 5 shows only when the patient has actually given a
    // relevant answer about direction or motivation — never as a general
    // standard paragraph just because the category exists in the logic (her
    // words: "lämnat ett relevant svar", "generellt standardstycke").
    //
    // The condition was `desiredOutcome.length > 0`. Q12 is `required: true`,
    // so that was true for EVERY patient — exactly the standard paragraph she
    // forbids. "Has answered" therefore cannot be what she means by
    // "lämnat ett relevant svar"; that is automatic.
    //
    // The only boundary the data carries: `other` is the catch-all option and
    // says nothing about direction on its own. A lone "Annat" without free
    // text is therefore no answer about what she wants — but if she writes
    // something in the free text, it IS an answer, and then it counts. Every
    // other option is in itself a statement about direction.
    //
    // This also keeps TES-476's point (a patient whose only answer is "Annat"
    // must not be silenced), but requires that she actually said what she
    // meant.
    direction: true,
    applies: (a) => {
      const picked = asArray(a.desiredOutcome);
      const substantive = picked.filter((v) => v !== "other");
      if (substantive.length > 0) return true;
      return (
        picked.includes("other") &&
        typeof a.desiredOutcomeOther === "string" &&
        a.desiredOutcomeOther.trim().length > 0
      );
    },
    copy: {
      sv: "I dina svar har du också berättat vad som är viktigt för dig och vad du vill kunna förändra. Det ger en riktning för den fortsatta dialogen och hjälper oss att utgå från det som betyder mest i din vardag.",
      en: "In your answers you have also told us what matters to you and what you would like to change. That gives a direction for the continued conversation and helps us start from what means most in your everyday life.",
    },
  },
];

// Ö-138 — the ingress, verbatim.
const INGRESS: Record<"sv" | "en", string> = {
  sv: "Här är en sammanfattning av det du har berättat.",
  en: "Here is a summary of what you have told us.",
};

const V2_MAX_MODULES = 3;

// TES-1320 — the mirror of SUIT_DESIRED_SUPPORT (F-01 v11), from the F-01
// result doc's "KANONISKT TILLÄGG 2026-10-13 — SPEGLING AV ÖNSKAT STÖD". Both
// sentence frames are verbatim from Drive.
//
// PENDING Ö-532 (TES-475): the doc only says "[naturligt formulerat
// stödbehov]". The phrase per code and the "[A] och [B]" join below are our
// proposal in Ö-532, not approved copy. The English is ours too.
//
// It is personal feedback only: "aldrig i den medicinska klassificeringen,
// resultatstatusen eller CTA-routingen", and it must not suggest that the
// support is included or that medication will be offered. The frame only
// says what she told us and that we take it with us; nothing here promises
// an intervention.
//
// PENDING Ö-533 (assumed): the block is not counted in the module cap, is
// shown in "Det du har berättat för oss" and Din resa, and is left out of
// the compressed reflection.
//
// The keys are the concrete codes in the question's order, which is the
// order two picks are joined in. NOT_SURE_YET has its own sentence; OTHER is
// never mirrored: no sanitising of the free text is decided, and the doc
// allows leaving it out ("Om den inte kan användas utelämnas just den
// speglingen").
export const DESIRED_SUPPORT_PHRASES: Record<
  string,
  Record<"sv" | "en", string>
> = {
  MEDICATION_SUPPORT: {
    sv: "stöd med läkemedelsbehandling för din vikt",
    en: "support with medication treatment for your weight",
  },
  APPETITE_SIGNALS: {
    sv: "hjälp med hunger, mättnad och sug",
    en: "help with hunger, fullness and cravings",
  },
  EVERYDAY_FOOD: {
    sv: "stöd kring mat som fungerar i din vardag",
    en: "support with food that works in your everyday life",
  },
  MOVEMENT_EXERCISE: {
    sv: "stöd kring rörelse och träning",
    en: "support with movement and exercise",
  },
  SUSTAINABLE_HABITS: {
    sv: "hjälp att skapa vanor och rutiner som håller",
    en: "help building habits and routines that last",
  },
  STRESS_EMOTIONS_SELF_CRITICISM: {
    sv: "stöd kring stress, känslor eller självkritik",
    en: "support with stress, emotions or self-criticism",
  },
  STRUCTURE_FOLLOW_UP_DIRECTION: {
    sv: "struktur, uppföljning och hjälp att hålla riktningen",
    en: "structure, follow-up and help keeping on course",
  },
};

const DESIRED_SUPPORT_NOT_SURE = "NOT_SURE_YET";

const DESIRED_SUPPORT_COPY = {
  sv: {
    chosen: (phrase: string) =>
      `Du har också berättat att ${phrase} skulle göra störst skillnad för dig just nu. Det tar vi med oss när vi lär känna dig och formar stödet vidare.`,
    notSure:
      "Du behöver inte veta exakt vilket stöd du behöver redan nu. Det kan bli tydligare när vi lär känna dig och din situation bättre.",
    and: "och",
  },
  en: {
    chosen: (phrase: string) =>
      `You have also told us that ${phrase} would make the biggest difference for you right now. We will take that with us as we get to know you and shape the support going forward.`,
    notSure:
      "You do not need to know exactly what support you need yet. It may become clearer as we get to know you and your situation better.",
    and: "and",
  },
} as const;

/**
 * TES-1320 — the desired-support sentence for her answers, or null. Null for
 * every answer set without SUIT_DESIRED_SUPPORT (v10 and older), so older
 * results render exactly as before ("Äldre F-01-resultat utan
 * SUIT_DESIRED_SUPPORT ska renderas oförändrat").
 */
export function desiredSupportReflection(
  answers: Answers,
  locale: Locale,
): string | null {
  const lang = locale === "en" ? "en" : "sv";
  const picked = new Set(asArray(answers[DESIRED_SUPPORT_KEY]));
  const phrases = Object.entries(DESIRED_SUPPORT_PHRASES)
    .filter(([code]) => picked.has(code))
    .slice(0, 2)
    .map(([, phrase]) => phrase[lang]);
  const copy = DESIRED_SUPPORT_COPY[lang];
  if (phrases.length > 0) {
    return copy.chosen(phrases.join(` ${copy.and} `));
  }
  return picked.has(DESIRED_SUPPORT_NOT_SURE) ? copy.notSure : null;
}

// The reflection paragraphs: the given modules in order, with her
// desired-support sentence (if any) "efter speglingen av hinder och före
// mål/önskad förändring", i.e. right before module 5, or last without it.
function withDesiredSupport(
  modules: typeof V2_MODULES,
  answers: Answers,
  lang: "sv" | "en",
): string[] {
  const texts = modules.map((m) => m.copy[lang]);
  const support = desiredSupportReflection(answers, lang);
  if (support === null) return texts;
  const at = modules.findIndex((m) => m.direction);
  if (at === -1) return [...texts, support];
  return [...texts.slice(0, at), support, ...texts.slice(at)];
}

// The personal reflection — the modules her own answers earn, capped the way
// every patient's are. Shared by both results on purpose: Emelie's QA finding
// (2026-09-21) was that the transfer branch had REPLACED this, making a
// transfer patient the only one who never read her own needs, obstacles,
// problem areas, wishes and goals back. TES-1320: the desired-support
// sentence comes on top of the cap (Ö-533, assumed).
function personalModules(answers: Answers, lang: "sv" | "en"): string[] {
  return withDesiredSupport(
    V2_MODULES.filter((m) => m.applies(answers)).slice(0, V2_MAX_MODULES),
    answers,
    lang,
  );
}

// TES-1115 — Emelie 2026-10-05 (TES-1112 point 2): on a positive
// new-treatment result the personal reflection stays but is compressed to
// "högst två relevanta teman; ett sammanhängande stycke; högst tre korta meningar".
// No sentence is written for it: every sentence comes from her Ö-138 modules,
// which are two sentences each. One theme keeps its module whole (two
// sentences); two themes keep the first, observing sentence of each (two
// sentences, one per theme). Either way one paragraph of at most three
// sentences. Null when none of her answers earns a module. TES-1320: the
// desired-support sentence is never one of the themes (Ö-533, assumed).
const REFLECTION_MAX_THEMES = 2;

function firstSentence(text: string): string {
  const end = text.indexOf(". ");
  return end === -1 ? text : text.slice(0, end + 1);
}

export function compressedReflection(
  answers: Answers,
  locale: Locale,
): string | null {
  const lang = locale === "en" ? "en" : "sv";
  const themes = V2_MODULES.filter((m) => m.applies(answers))
    .slice(0, REFLECTION_MAX_THEMES)
    .map((m) => m.copy[lang]);
  if (themes.length === 0) return null;
  if (themes.length === 1) return themes[0]!;
  return themes.map(firstSentence).join(" ");
}

// TES-733 — the routing block after her feedback, verbatim from the F-01
// doc's topmost block,
// "KANONISK ROUTINGCOPY — RECEPTÖVERTAG EFTER ÅTERKOPPLING". It replaces the
// variant mapping entirely rather than decorating it, because the ordinary
// result leads to booking a video appointment and this one says
// "Du behöver inte boka ett vårdmöte." It is part of the feedback, never a
// separate page; the CTA label lives with each client (app:
// "Fortsätt med din hälsodeklaration", web: "Fortsätt i Ella-appen").
//
// TES-1115 — Ö-335 answered "b" (Emelie 2026-10-05): two headings, not one.
// The main heading is TES-1112 point 9's
// "Ella kan vara relevant för din fortsatta behandling"; Ö-157's
// "Nästa steg för ditt receptövertag" stays as the routing heading under it.
// Her condition: they must not read as two competing main messages; the
// first orients, the second says what happens next.
//
// TES-1150 (Emelie 2026-10-07, §4) — the routing block now says, very
// clearly, that the review costs nothing: her heading replaces Ö-157's
// "Nästa steg för ditt receptövertag", and her two paragraphs follow the
// sentence that names what she told us. "Undvik ordet 'nu'", so nothing
// here reads as if she would pay for the review later.
//
// TES-1220 — her heading and two paragraphs were only ever served on Hem's
// "För dig idag" card, and Emelie's 2026-10-09 card copy
// (TRANSFER_OPTIONAL_MEETING_COPY below) replaces them there wholesale, so
// they are gone. Only the compact line stays: Dina steg's rung still says it,
// and "inget vårdmöte behöver bokas" agrees with her newer text (the
// voluntary meeting is never something she needs to book).
export const TRANSFER_REVIEW_FREE_COPY = {
  // "Kompakt copy vid inskick".
  compact: {
    sv: "Kostnadsfri läkargranskning – inget vårdmöte behöver bokas.",
    en: "Free review by a doctor – no consultation needs to be booked.",
  },
} as const;

// TES-1220 (Emelie 2026-10-09, TES-475) — the transfer patient's Hem card,
// before and after she submits, verbatim. "Must read visually as an optional
// add-on, not the patient's next mandatory step": it lives in "För dig idag"
// (never §2, which owns her next step) and its CTA is the card's secondary
// button, to Din vård's 395 kr meeting card (`health/care`) — never
// `book-doctor-meeting`, which is onboarding's first_consult and TES-851
// refuses for her.
//
// `optionalMeeting` and the CTA travel together: the paragraph promises a
// meeting she can book for 395 kr, so it is shown only where that is true
// (see HomeSummaryService). English is ours, pending approval.
export const TRANSFER_OPTIONAL_MEETING_COPY = {
  title: {
    sv: "Du behöver inte boka ett vårdmöte",
    en: "You do not need to book a consultation",
  },
  body: {
    sv: "Ditt receptövertag bedöms av en läkare utifrån uppgifterna du skickar in. Det kostar inget att få receptövertagsärendet bedömt och ett vårdmöte krävs normalt inte.",
    en: "A doctor assesses your prescription transfer based on the details you submit. Having your prescription transfer assessed costs nothing, and a consultation is normally not required.",
  },
  optionalMeeting: {
    sv: "Om du själv vill prata med en läkare kan du boka ett frivilligt digitalt vårdmöte för 395 kr. Det frivilliga mötet påverkar inte läkarens bedömning av ditt receptövertag.",
    en: "If you would like to talk to a doctor yourself, you can book a voluntary digital consultation for SEK 395. The voluntary meeting does not affect the doctor's assessment of your prescription transfer.",
  },
  ctaLabel: {
    sv: "Boka ett frivilligt vårdmöte",
    en: "Book a voluntary consultation",
  },
  ctaPath: "carebyella://health/care",
} as const;

// Only the active choice routes (Emelie 2026-09-16; Ö-154). The stored
// v3–v7 values `ja`/`osaker` answered a different question and never do.
const TRANSFER_TRIGGERS = new Set(["ja_fortsatt"]);

/** Did the patient actively choose to have Ella take over her prescription? */
export function isPrescriptionTransfer(answers: Answers): boolean {
  const v = answers.prescriptionTransfer;
  if (typeof v !== "string") return false;
  return TRANSFER_TRIGGERS.has(v);
}

interface ResultOptions {
  consultFeeMinor: number | undefined;
  channel: F01ResultChannel;
  pricing: F01ResultPricing | undefined;
}

function buildV2Summary(
  answers: Answers,
  locale: Locale,
  omitIngress: boolean,
  options: ResultOptions,
): HealthProfileSummary {
  const lang = locale === "en" ? "en" : "sv";
  // TES-1161 — every result's copy is the canonical page's
  // (f01-result-page.ts). The fields below carry it to the clients that
  // render this summary rather than the page: app 2.0.6 and older, and Din
  // resa. 2.0.7 and the web render the page itself.
  const page = buildF01ResultPage({
    answers,
    locale,
    channel: options.channel,
    consultFeeMinor: options.consultFeeMinor,
  });
  if (page?.outcome === "transfer") {
    return transferSummary(answers, lang, page);
  }
  const raw = computeBmiFromAnswers(answers);
  const bmi = raw === null ? null : roundBmi(raw);
  if (page && F01_MEETING_OUTCOMES.has(page.outcome) && bmi !== null) {
    return newTreatmentSummary(answers, locale, bmi, page, options);
  }
  if (page?.outcome === "needs_more_info") {
    // "Ingen bokning eller betalning öppnas före komplettering." App builds
    // before 2.0.7 send every letter but C and D to their booking button, and
    // D to the quiz (their only way to answer again), so D it is: no booking,
    // and the server lets a new submission replace it.
    return {
      heading: page.heading,
      reflections: page.paragraphs,
      expertNote: page.paragraphs.join("\n\n"),
      variant: "D",
      bmi,
      track: "standard",
    };
  }
  if (page) {
    // A stop (§5, §9–§11). The clients show their stop page from the routing
    // before this summary; C keeps every older build off its booking button.
    return {
      heading: page.heading,
      reflections: page.paragraphs,
      expertNote: "",
      variant: "C",
      bmi,
      track: isPrescriptionTransfer(answers)
        ? "prescription_transfer"
        : "standard",
    };
  }
  // Weight or height missing (variant D): no BMI band to communicate.
  const copy = VARIANT_COPY.D[lang];
  const modules = personalModules(answers, lang);
  return {
    heading: copy.heading,
    reflections: omitIngress
      ? [copy.body, ...modules]
      : [INGRESS[lang], copy.body, ...modules],
    expertNote: copy.nextStep,
    variant: "D",
    bmi,
    track: "standard",
  };
}

// TES-1161 — §15. Older app builds render a transfer result as heading, the
// closed "Din återkoppling" box over `reflections`, then `expertNote` open
// above "Fortsätt med din hälsodeklaration". So her page goes, in order, in
// `expertNote` (BMI line, both paragraphs, the five steps) and her personal
// reflection stays in the box, as before (Emelie, QA 2026-09-21: a transfer
// patient reads her own answers back like everyone else).
function transferSummary(
  answers: Answers,
  lang: "sv" | "en",
  page: F01ResultPage,
): HealthProfileSummary {
  const steps = page.steps
    ? [
        page.steps.title,
        page.steps.items.map((step, i) => `${i + 1}. ${step}`).join("\n"),
      ]
    : [];
  const raw = computeBmiFromAnswers(answers);
  return {
    heading: page.heading,
    reflections: personalModules(answers, lang),
    expertNote: [...page.paragraphs, ...steps].join("\n\n"),
    bmi: raw === null ? null : roundBmi(raw),
    ...(page.bmiText ? { bmiText: page.bmiText } : {}),
    track: "prescription_transfer",
  };
}

// A booking result (§1, §3, §4, §6, §7 of the canonical copy) for the app
// builds that render the summary rather than the page.
//
// Ö-336 (Emelie 2026-10-05) set the pattern: an older build shows new text in
// the server fields it already renders, in its own order. 2.0.4/2.0.5 render
// `heading`, the closed "Din återkoppling" box over `reflections`, then
// `expertNote` as plain text above "Boka vårdmöte"; so `expertNote` carries
// the page's paragraphs and the meeting module, one paragraph each, and the
// price is read before booking. 2.0.6 renders `newTreatment` (TES-1126's
// layout: heading → `bmiText` → `bandText` → `nextStepHeading` →
// `meetingValue` → `safety` → `price` → "Välj tid" → `sections`).
// `reflections` keeps the compressed reflection for the box; the personal
// summary is the "told" section.
function newTreatmentSummary(
  answers: Answers,
  locale: Locale,
  bmi: number,
  page: F01ResultPage,
  options: ResultOptions,
): HealthProfileSummary {
  const { consultFeeMinor, channel, pricing } = options;
  const lang = locale === "en" ? "en" : "sv";
  const meeting = page.meeting!;
  const reflection = compressedReflection(answers, locale);
  // TES-1161 — app 2.0.6 renders heading → `bmiText` (bold) → `bandText` →
  // `nextStepHeading` → `meetingValue` → `safety` → `price` → "Välj tid" →
  // `sections`. Her paragraphs other than the BMI line go in `bandText`, in
  // her order; the meeting module fills the rest. 2.0.7 renders the page.
  const newTreatment = {
    bmiText: page.bmiText ?? "",
    bandText: page.paragraphs.filter((p) => p !== page.bmiText).join("\n\n"),
    reflection,
    meetingValue: meeting.body,
    price: meeting.price,
    safety: meeting.noSubscription,
    nextStepHeading: meeting.heading,
    sections: resultSections(
      answers,
      lang,
      channel,
      consultFeeMinor,
      pricing,
      meeting.steps,
    ),
  };
  return {
    heading: page.heading,
    // The Ö-138 ingress is not carried here: it is not a reflection
    // sentence, and her cap of three sentences leaves no room for it.
    reflections: reflection === null ? [] : [reflection],
    expertNote: [
      ...page.paragraphs,
      meeting.heading,
      meeting.body,
      meeting.price,
      meeting.noSubscription,
    ]
      .filter((line): line is string => Boolean(line))
      .join("\n\n"),
    variant: bmi >= 30 ? "A" : "E",
    bmi,
    track: "standard",
    newTreatment,
  };
}

// TES-1126 — the three closed sections, in her order. "told" is left out when
// none of her answers earns a module (an ingress announcing a summary that
// never comes reads as a bug); "cost" is left out when the caller passed no
// prices, so no amount is ever hardcoded.
function resultSections(
  answers: Answers,
  lang: "sv" | "en",
  channel: F01ResultChannel,
  consultFeeMinor: number | undefined,
  pricing: F01ResultPricing | undefined,
  howSteps: string[],
): F01ResultSection[] {
  const copy = RESULT_SECTION_COPY[lang];
  const locale: Locale = lang;
  const sections: F01ResultSection[] = [];
  // TES-1320 — her desired-support sentence is part of this section, so it
  // alone is enough to show it.
  const modules = withDesiredSupport(
    V2_MODULES.filter((m) => m.applies(answers)),
    answers,
    lang,
  );
  if (modules.length > 0) {
    sections.push({
      key: "told",
      title: copy.toldTitle,
      // The ingress appears exactly once, here (the doc: "Ingressen får
      // förekomma en gång").
      paragraphs: [INGRESS[lang], ...modules],
      steps: [],
      notes: [],
    });
  }
  sections.push({
    key: "how",
    title: copy.howTitle,
    paragraphs: channel === "web" ? [copy.howWebIntro] : [],
    steps: [...howSteps],
    notes: [],
  });
  if (consultFeeMinor !== undefined && pricing) {
    const overview = priceOverview(
      locale,
      {
        consultMinor: consultFeeMinor,
        coreSubscriptionMinor: pricing.coreSubscriptionMinor,
        maintenanceMinor: pricing.maintenanceMinor,
        labTestMinor: pricing.labTestMinor,
        launchOfferFreeMonths: pricing.launchOfferFreeMonths,
      },
      { separateCampaign: pricing.separateCampaign },
    );
    const flat = overview.campaign
      ? [overview.campaign, ...overview.blocks]
      : overview.blocks;
    sections.push({
      key: "cost",
      title: overview.title,
      paragraphs: flattenPriceOverview(flat),
      steps: [],
      notes: [],
      blocks: overview.blocks,
      ...(overview.campaign ? { campaign: overview.campaign } : {}),
    });
  }
  return sections;
}

/**
 * TES-1161 — the saved feedback under Din resa: her result page's paragraphs
 * (the BMI line among them), then, after a booking or transfer result, the
 * same personal summary the result shows (the doc: "Samma versionssatta
 * patientåterkoppling återanvänds under Din resa"). The meeting module and
 * the actions belong to the result page, not to the saved feedback. Null
 * when the answers have no page (weight or height missing).
 */
export function f01FeedbackParagraphs(
  answers: Answers,
  locale: Locale,
  // TES-1656 (TES-1653 §4): the answers were given on the optional care
  // meeting track (TES-1601). A positive result then led to the health
  // declaration, not to a meeting, and the saved feedback must say so.
  // The caller decides this per answer set, so an older result keeps the
  // meeting it was given under.
  options: { careMeetingOptional?: boolean } = {},
): string[] | null {
  const page = buildF01ResultPage({ answers, locale, channel: "app" });
  if (!page) return null;
  if (
    options.careMeetingOptional === true &&
    isF01OptionalTrackOutcome(page.outcome)
  ) {
    // E4 texts 1–3: the optional result's own paragraphs, without the app
    // page's greeting and button (the web channel's), then the same personal
    // summary as below.
    const optional = buildF01ResultPage({
      answers,
      locale,
      channel: "web",
      careMeetingOptional: true,
    });
    const told = resultSections(
      answers,
      locale === "en" ? "en" : "sv",
      "app",
      undefined,
      undefined,
      [],
    ).find((section) => section.key === "told");
    return [...(optional?.paragraphs ?? []), ...(told ? told.paragraphs : [])];
  }
  if (page.outcome === "transfer") {
    return [
      ...page.paragraphs,
      ...personalModules(answers, locale === "en" ? "en" : "sv"),
    ];
  }
  if (!F01_MEETING_OUTCOMES.has(page.outcome)) return page.paragraphs;
  // TES-1234 — the saved feedback opens with "Dina svar visar att du kan gå
  // vidare till ett digitalt vårdmöte." instead of the result page's
  // "… ett relevant nästa steg." (§1 only: no other outcome carries that
  // line). The result page itself keeps its own text.
  const lang: Locale = locale === "en" ? "en" : "sv";
  const resultOpening = F01_RESULT_COPY[lang].outcomes.treatment.paragraphs[0];
  const paragraphs =
    page.outcome === "treatment"
      ? page.paragraphs.map((p) =>
          p === resultOpening ? F01_SAVED_FEEDBACK_OPENING[lang] : p,
        )
      : page.paragraphs;
  const told = resultSections(
    answers,
    locale === "en" ? "en" : "sv",
    "app",
    undefined,
    undefined,
    [],
  ).find((section) => section.key === "told");
  return [...paragraphs, ...(told ? told.paragraphs : [])];
}
